// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
// @ts-strict
import type {
  IntervalName,
  SimpleGenericIntervalNumber,
  MajorMinorIntervalGroupNumber,
  PerfectIntervalGroupNumber,
  IntervalQuality,
} from 'shared/definitions/musicTheory/intervals';
import {
  genericToChromaticIntervalsDictionary,
  intervalQualityLongNames,
  parseIntervalString,
} from 'shared/definitions/musicTheory/intervals';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import {
  accidentalIntegers,
  noteLetterIntegers,
} from 'shared/definitions/musicTheory/notesAndAccidentals';
import { findKey } from 'shared/src/utils/findKey';
import utils from '../utils';
import Note from './note';
import type KeySignature from './keySignature';

/**
 * ## Interval
 *
 * A representation of a musical interval, with functions for
 * calculation and construction.
 */
export default class Interval {
  public direction!: 1 | -1;

  public quality!: 'dd' | 'm' | 'M' | 'A' | 'd' | 'P' | 'AA';

  public genericInterval!: MajorMinorIntervalGroupNumber | PerfectIntervalGroupNumber;

  public simpleGenericInterval!: SimpleGenericIntervalNumber;

  public octaves!: number;

  public compound!: boolean;

  // Public constructor
  constructor(intervalName?: IntervalName | Interval) {
    if (typeof intervalName === 'undefined') {
      intervalName = 'P1';
    }
    this.setName(intervalName);
  }

  setName(intervalOrIntervalName: IntervalName | Interval) {
    const intervalName =
      intervalOrIntervalName instanceof Interval
        ? intervalOrIntervalName.getName()
        : intervalOrIntervalName;

    const { quality, genericInterval, direction } = parseIntervalString(intervalName);
    this.direction = direction;
    this.quality = quality;
    this.genericInterval = genericInterval;
    this.simpleGenericInterval = Interval.makeGenericSimpleInt(genericInterval);
    this.octaves = Interval.getOctavesFromGeneric(genericInterval);
    this.compound = !!this.octaves;
  }

  clone(): Interval {
    return new Interval(this.getName());
  }

  /**
   * Set this interval based on the distance between two notes. Throws if
   * there is no valid interval name for that pair of notes.
   * @param note1
   * @param note2
   */
  setInterval(note1: NoteName | Note, note2: NoteName | Note) {
    const interval = Interval.getInterval(note1, note2);
    if (interval) {
      this.setName(interval);
    } else {
      throw new Error(`No valid interval from ${note1} to ${note2}`);
    }
  }

  /**
   * Change the direction of this interval.
   */
  setDirection(direction: 1 | -1): Interval {
    if (direction === 1 || direction === -1) {
      this.direction = direction;
      return this;
    }
    throw new Error('Direction must be 1 or -1.');
  }

  // ### Interval String Representation Functions

  toString(): IntervalName {
    return this.getName();
  }

  toLongString(): string {
    return this.longName();
  }

  getName(): IntervalName {
    const direction = this.direction === -1 ? '-' : '';
    const quality = this.quality;
    const genericInterval = this.genericInterval;
    return `${direction}${quality}${genericInterval}` as IntervalName;
  }

  /**
   * String representation of interval direction: 'ascending' or 'descending'
   */
  ascendingDescending(): string {
    return this.direction === -1 ? 'descending' : 'ascending';
  }

  /**
   * "above" if ascending interval, "below" if descending
   */
  aboveBelow(): string {
    return this.direction === -1 ? 'below' : 'above';
  }

  /**
   * Perfect 2nd, Perfect Octave, etc...
   */
  longName(): string {
    const quality = intervalQualityLongNames[this.quality];
    return `${quality} ${this.longGenericName()}`;
  }

  /**
   * Returns 'a' or 'an' based on the following word.
   */
  aOrAn(): 'a' | 'an' {
    return utils.aOrAn(this.longName());
  }

  /**
   * 8ve, U, 1st, 2nd, etc...
   */
  ordinalGenericName(): string {
    switch (this.genericInterval) {
      case 8:
        return '8ve';
      case 1:
        return 'U';
      default:
        return utils.ordinal(this.genericInterval);
    }
  }

  /**
   * Unison, Octave, 1st, 2nd, etc...
   */
  longGenericName(): string {
    let ordinal;
    switch (this.genericInterval) {
      case 8:
        ordinal = 'octave';
        break;
      case 1:
        ordinal = 'unison';
        break;
      default:
        ordinal = this.ordinalGenericName();
    }
    return ordinal;
  }

  /**
   * 'half step', 'whole step', 'perfect 4th', 'perfect octave', etc...
   */
  halfWholeName(): string {
    if (this.genericInterval === 2) {
      if (this.quality === 'M') return 'whole step';
      if (this.quality === 'm') return 'half step';
    }
    if (this.genericInterval === 1 && this.quality === 'A') {
      return 'half step';
    }
    return this.longName();
  }

  /**
   * 'diatonic half step', 'whole step', 'Perfect octave', 'Major 3rd', etc...
   */
  longHalfWholeName(): string {
    if (this.genericInterval === 2) {
      if (this.quality === 'M') return 'whole step';
      if (this.quality === 'm') return 'diatonic half step';
    }
    if (this.genericInterval === 1 && this.quality === 'A') {
      return 'chromatic half step';
    }
    return this.longName();
  }

  // ### INTERVAL METHODS
  //
  // Methods for determining or calculating intervals

  /**
   * Get the inversion of this interval. Always returns an ascending, simple interval.
   */
  getInversion(): Interval {
    let genericInterval: SimpleGenericIntervalNumber;
    let quality: IntervalQuality;

    switch (this.simpleGenericInterval) {
      case 1:
        genericInterval = 8;
        break;
      case 2:
        genericInterval = 7;
        break;
      case 3:
        genericInterval = 6;
        break;
      case 4:
        genericInterval = 5;
        break;
      case 5:
        genericInterval = 4;
        break;
      case 6:
        genericInterval = 3;
        break;
      case 7:
        genericInterval = 2;
        break;
      case 8:
        genericInterval = 1;
        break;
      default:
        throw Error(`Cannot invert ${this}`);
    }

    switch (this.quality) {
      case 'm':
        quality = 'M';
        break;
      case 'M':
        quality = 'm';
        break;
      case 'P':
        quality = 'P';
        break;
      case 'A':
        quality = 'd';
        break;
      case 'd':
        quality = 'A';
        break;
      case 'dd':
        quality = 'AA';
        break;
      case 'AA':
        quality = 'dd';
        break;
      default:
        throw Error(`Cannot invert interval ${this}`);
    }

    return new Interval(`${quality}${genericInterval}` as IntervalName);
  }

  /**
   * Gets the semitones of this interval. Always a positive number.
   */
  getSemiTones(): number {
    const qualities = genericToChromaticIntervalsDictionary[this.simpleGenericInterval];
    let semiTones = Number(
      Object.keys(qualities).find((halfStepsString) => {
        const quality = qualities[Number(halfStepsString)];
        return quality === this.quality;
      })
    );
    // utils.arraySearch(this.quality, qualities);
    semiTones = Number(semiTones) + this.octaves * 12;
    return semiTones;
  }

  /**
   * Returns an interval that is enharmonically equivalent to this one,
   * or false if one doesn't exist. Resulting interval will have the
   * same direction as this. ONLY WORKS ON SIMPLE INTERVALS.
   */
  getEnharmonicallyEquivalentInterval(): Interval | false {
    const nameWithoutDirection = `${this.quality}${this.simpleGenericInterval}`;

    const enharmonicInterval = [
      ['P1', 'd2'],
      ['m2', 'A1'],
      ['M2', 'd3'],
      ['m3', 'A2'],
      ['M3', 'd4'],
      ['P4', 'A3'],
      ['A4', 'd5'],
      ['P5', 'd6'],
      ['m6', 'A5'],
      ['M6', 'd7'],
      ['m7', 'A6'],
      ['M7', 'd8'],
    ]
      .find((intervals) => intervals.indexOf(nameWithoutDirection) !== -1)
      ?.find((interval) => interval.toString() !== nameWithoutDirection);
    if (!enharmonicInterval) return false;
    return new Interval(enharmonicInterval as IntervalName).setDirection(this.direction);
  }

  isSimple(): boolean {
    return this.genericInterval <= 8;
  }

  isCompound(): boolean {
    return this.genericInterval > 8;
  }

  equals(that: Interval | IntervalName): boolean {
    that = Interval.stringToInterval(that);
    return this.getName() === that.getName();
  }

  equalsIgnoreDirection(that: IntervalName | Interval): boolean {
    that = Interval.stringToInterval(that);
    return this.genericInterval === that.genericInterval && this.quality === that.quality;
  }

  equalsGenericIgnoreDirection(that: IntervalName | Interval): boolean {
    return (
      Math.abs(this.genericInterval) === Math.abs(Interval.stringToInterval(that).genericInterval)
    );
  }

  equalsEnharmonically(that: IntervalName | Interval): boolean {
    that = Interval.stringToInterval(that);
    return this.getSemiTones() === that.getSemiTones();
  }

  isGreaterThan(that: IntervalName | Interval): boolean {
    that = Interval.stringToInterval(that);
    return this.getSemiTones() > that.getSemiTones();
  }

  isLessThan(that: IntervalName | Interval): boolean {
    that = Interval.stringToInterval(that);
    return this.getSemiTones() < that.getSemiTones();
  }

  isGreaterThanOrEquals(that: IntervalName | Interval): boolean {
    that = Interval.stringToInterval(that);
    return this.getSemiTones() >= that.getSemiTones();
  }

  isLessThanOrEquals(that: IntervalName | Interval): boolean {
    that = Interval.stringToInterval(that);
    return this.getSemiTones() <= that.getSemiTones();
  }

  static stringToInterval(that: IntervalName | Interval): Interval {
    if (that instanceof Interval) {
      return that;
    }
    return new Interval(that);
  }

  /**
   * Get the generic interval between two notes. May return a number bigger than 15.
   */
  static getGenericInterval(noteOrName1: NoteName | Note, noteOrName2: NoteName | Note): number {
    const note1 = Note.stringToNote(noteOrName1);
    const note2 = Note.stringToNote(noteOrName2);
    let note1int = noteLetterIntegers[note1.letter];
    note1int += note1.octave * 7;
    let note2int = noteLetterIntegers[note2.letter];
    note2int += note2.octave * 7;
    let genericInterval = note2int - note1int;
    if (genericInterval >= 0) genericInterval++;
    else genericInterval--;
    return genericInterval;
  }

  /**
   * How many octaves (beyond 1) are contained in the generic interval?
   * Always returns a postive number.
   * ```typescript
   *  getOctavesFromGeneric(8); // ==> 0
   *  getOctavesFromGeneric(9); // ==> 1
   *  getOctavesFromGeneric(-9); // ==> 1
   *  getOctavesFromGeneric(15); // ==> 1
   *  getOctavesFromGeneric(16); // ==> 2
   * ```
   */
  static getOctavesFromGeneric(genericInterval: number): number {
    if (Math.abs(genericInterval) === 8 || Math.abs(genericInterval) === 1) return 0;
    return Math.floor((Math.abs(genericInterval) - 2) / 7);
  }

  /**
   * Covert a generic interval of any size (postive or negative) into a simple generic
   * interval number. Always returns a positive number.
   * ```typescript
   *   makeGenericSimpleInt(9); // ==> 2
   *   makeGenericSimpleInt(2); // ==> 2
   *   makeGenericSimpleInt(-15); // ==> 8
   * ```
   */
  static makeGenericSimpleInt(genericInterval: number): SimpleGenericIntervalNumber {
    let simpleGenericInterval = ((Math.abs(genericInterval) - 1) % 7) + 1;
    if (simpleGenericInterval === 1 && genericInterval >= 8) simpleGenericInterval = 8;
    return simpleGenericInterval as SimpleGenericIntervalNumber;
  }

  /**
   * Get the interval between two notes. Returns false if none exists.
   */
  static getInterval(noteOrName1: NoteName | Note, noteOrName2: NoteName | Note): Interval | false {
    const note1 = Note.stringToNote(noteOrName1);
    const note2 = Note.stringToNote(noteOrName2);

    // calculate the generic interval:
    let genericInterval = Interval.getGenericInterval(note1, note2);

    // calculate the direction:
    const direction = note2.getMidiNumber() >= note1.getMidiNumber() ? 1 : -1;
    //      var directionText = (genericInterval > 0) ? "" : "-";
    const directionText = direction === 1 ? '' : '-';

    genericInterval = Math.abs(genericInterval);

    // calculate the simple version:
    const simpleGenericInterval = Interval.makeGenericSimpleInt(genericInterval);

    // console.log("Generic interval: " + genericInterval);
    const octaves = Interval.getOctavesFromGeneric(genericInterval);
    // console.log("octaves: " + octaves);

    // calculate the half steps between these:
    const semiTones =
      direction === 1
        ? note2.getMidiNumber() - note1.getMidiNumber() - 12 * octaves
        : note1.getMidiNumber() - note2.getMidiNumber() - 12 * octaves;

    const quality = genericToChromaticIntervalsDictionary[simpleGenericInterval][semiTones];

    if (quality === undefined) {
      return false;
    }
    return new Interval(`${directionText}${quality}${genericInterval}` as IntervalName);
  }

  /**
   * Get note at a generic interval. Keeps original accidnetal, unless
   * keySignature argument is provided, in which case applies the key
   * signature accidentals to the new note.
   * @param noteOrName1 The starting note
   * @param intervalInput the interval, as string, number or Interval
   * @param keySignature If provided, apply the accidental from the KeySig
   * @returns
   */
  static getNoteAtGenericInterval(
    noteOrName1: NoteName | Note,
    intervalInput: IntervalName | number | Interval,
    keySignature?: KeySignature
  ): Note {
    // If note's not of type Note create it:
    const note1 = Note.stringToNote(noteOrName1);

    let genericInterval: number;
    // Is it a number? Let it be.
    if (typeof intervalInput === 'number') {
      genericInterval = intervalInput;
      // Is it a number cast as a string? Cast as a number
    } else if (utils.isString(intervalInput) && utils.isNumeric(intervalInput)) {
      genericInterval = Number(intervalInput);
      // Is it an interval string?
    } else if (utils.isString(intervalInput)) {
      const interval = new Interval(intervalInput);
      genericInterval = interval.direction * interval.genericInterval;
    } else if (intervalInput instanceof Interval) {
      genericInterval = intervalInput.direction * intervalInput.genericInterval;
    } else {
      throw new Error(
        'getNoteAtGenericInterval requires a generic interval number, string interval number, string interval name, or Interval'
      );
    }

    // change musical interval to mathematical interval (e.g. don't
    // double count the starting note)
    if (genericInterval < 0) genericInterval++;
    if (genericInterval > 0) genericInterval--;

    // calculate note at generic int:
    const note1int = noteLetterIntegers[note1.letter];
    let note2int = note1int + genericInterval; // because we count the starting note
    const octaveChange = Math.floor(note2int / 7);
    note2int %= 7;
    if (note2int < 0) {
      note2int += 7;
    }
    // change the number to a letter name
    const note2letter = findKey(noteLetterIntegers, note2int);
    // const note2letter = utils.arraySearch(note2int, noteLetterIntegers);
    const note2octave = note1.octave + octaveChange;
    // create a note at the generic interval with the same accidental, assuring
    // that we won't through NaI right away
    const noteAtGeneric = new Note({
      letter: note2letter,
      octave: note2octave,
      accidental: note1.accidental,
    });
    if (keySignature) return keySignature.applyKeySignatureToNote(noteAtGeneric);
    return noteAtGeneric;
  }

  static getNoteAt(
    noteOrName1: NoteName | Note,
    intervalInput: IntervalName | Interval
  ): Note | false {
    // If note's not of type Note create it:
    const note1 = Note.stringToNote(noteOrName1);

    // if intervalInput is not of type Interval, create it
    const interval = Interval.stringToInterval(intervalInput);

    // create a note at the generic interval with same accidental:
    const note2 = Interval.getNoteAtGenericInterval(
      note1,
      interval.simpleGenericInterval * interval.direction
    );

    // track any octave change difference:
    const octaveChange =
      Math.floor((interval.genericInterval - interval.simpleGenericInterval) / 7) *
      interval.direction;

    const simpleGenericInterval = interval.simpleGenericInterval;

    // test the interval quality
    const goalQualityInt = utils.arraySearch(
      interval.quality,
      genericToChromaticIntervalsDictionary[simpleGenericInterval]
    );
    const testQualityInt = Math.abs(note1.getMidiNumber() - note2.getMidiNumber());
    // determine how many accidentals (if any) need to be changed:
    // positive if size should increase; negative if decrease
    const sizeChange = Number(goalQualityInt) - testQualityInt;

    // change note2 accidental if possible; return false if not
    let accidentalInt = accidentalIntegers[note2.accidental];
    accidentalInt += sizeChange * interval.direction;

    // if that accidental exists, return the note:
    if (accidentalInt >= -2 && accidentalInt <= 2) {
      note2.accidental = findKey(accidentalIntegers, accidentalInt)!;
      note2.octave += octaveChange;
      return note2;
    }
    return false;
  }
}
